import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { normalizeRoleCode } from '../utils/roleUtils';

/** 判断某模块是否被云控允许（与 MainLayout 中的逻辑保持一致） */
function isPageAllowed(page: string, allowedPages: string[] | null): boolean {
  if (!Array.isArray(allowedPages)) return true; // null = 全部允许
  if (allowedPages.length === 0) return false;   // [] = 全部禁止
  return allowedPages.includes(page);
}

/**
 * 根据角色和云控 allowedPages，计算该用户登录后应跳转的目标路径。
 * 如果首选页面被云控禁止，则依次尝试备选页面，最后返回 /dashboard。
 */
function resolveHomePath(
  roleCode: string,
  storeIds: number[],
  allowedPages: string[] | null,
): string {
  // 收银员：始终跳收银台，收银台页面内部处理云控禁用的情况
  if (roleCode === 'cashier') {
    return '/cashier';
  }

  // 超级管理员：优先进总后台（/admin/dashboard 不受云控约束）
  if (roleCode === 'super_admin') {
    return '/admin/dashboard';
  }

  // 店长 / 管理员：按店铺数确定跳转目标
  if (roleCode === 'admin') {
    if (Array.isArray(storeIds) && storeIds.length === 1) {
      return `/stores/${storeIds[0]}/dashboard`;
    }
    if (Array.isArray(storeIds) && storeIds.length > 1) {
      return '/select-store';
    }
    return '/welcome';
  }

  // 财务主管：可访问财务全部子项、库存、报表、采购查看，默认进销售数据
  if (roleCode === 'manager') {
    return '/finance/sales';
  }

  // 库管：按云控权限依次尝试库存相关模块
  if (roleCode === 'warehouse') {
    const candidates: Array<{ page: string; path: string }> = [
      { page: 'inventory',   path: '/stock/overview' },
      { page: 'purchase',    path: '/purchase/orders' },
      { page: 'stocktaking', path: '/stock/stocktaking' },
    ];
    for (const c of candidates) {
      if (isPageAllowed(c.page, allowedPages)) return c.path;
    }
    return '/dashboard';
  }

  if (roleCode === 'purchaser') {
    const candidates: Array<{ page: string; path: string }> = [
      { page: 'purchase', path: '/purchase/orders' },
      { page: 'inventory', path: '/stock/overview' },
    ];
    for (const c of candidates) {
      if (isPageAllowed(c.page, allowedPages)) return c.path;
    }
    return '/dashboard';
  }

  // 其他未知角色：按云控权限依次尝试
  const candidates: Array<{ page: string; path: string }> = [
    { page: 'inventory',   path: '/stock/overview' },
    { page: 'purchase',    path: '/purchase/orders' },
    { page: 'members',     path: '/members' },
    { page: 'customers',   path: '/customers' },
    { page: 'orders',      path: '/yg-orders' },
    { page: 'stocktaking', path: '/stock/stocktaking' },
    { page: 'settings',    path: '/settings' },
  ];
  for (const c of candidates) {
    if (isPageAllowed(c.page, allowedPages)) return c.path;
  }

  // 所有模块都被禁止，回到仪表盘（始终可见）
  return '/dashboard';
}

/**
 * PostLoginRedirector
 * 监听 auth + activation 状态，根据角色/店铺数/云控权限智能跳转。
 * 放置在 <AppContent/> 顶部，只负责路由跳转，不渲染 UI。
 */
const PostLoginRedirector: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { allowedPages, loaded: activationLoaded } = useSelector((state: RootState) => state.activation);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // 仅在根路径或登录页等"无上下文"路径时执行自动跳转
    const silentPaths = ['/', '/login'];
    if (!silentPaths.includes(location.pathname)) return;

    const roleCode = normalizeRoleCode((user as any).role_code || (user as any).role);

    // 超级管理员不依赖 allowedPages，直接跳转
    if (roleCode === 'super_admin') {
      navigate('/admin/dashboard', { replace: true });
      return;
    }

    // 其他角色需等待 allowedPages 加载完毕，避免因 null 误判为全部允许后再跳错
    if (!activationLoaded) return;

    const storeIds = (user as any).storeIds || (user as any).associatedStoreIds || [];
    const target = resolveHomePath(roleCode, storeIds, allowedPages);
    navigate(target, { replace: true });
  }, [isAuthenticated, user, activationLoaded, allowedPages, navigate, location.pathname]);

  return null;
};

export default PostLoginRedirector;
